import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  AlertTriangle, Building2, Clock, Flag, Globe, Instagram, Loader2, Mail, MapPin, MessageSquare, Phone, Sparkles,
} from 'lucide-react';
import api from '@/lib/axios';
import { useSeo, useJsonLd } from '../hooks/useSeo';
import MarketplaceFooter from '@/components/marketplace/MarketplaceFooter';
import ReportListingModal from '@/components/marketplace/ReportListingModal';
import ConversationStartForm from '@/components/marketplace/ConversationStartForm';
import { AdSlot } from '@/components/marketplace/ads/AdSlot';
import { useConversationsEnabled } from '@/lib/conversations';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const resolveImg = (src) => {
  if (!src) return '';
  if (src.startsWith('data:') || src.startsWith('http')) return src;
  return src.startsWith('/api/') ? `${BACKEND_URL}${src}` : src;
};

export default function MarketplacePartnerDetailPage() {
  const { slug } = useParams();
  const [item, setItem] = useState(null);
  const [err, setErr] = useState('');
  const [assunto, setAssunto] = useState('');
  const [showReport, setShowReport] = useState(false);
  const conversasAtivas = useConversationsEnabled();

  useEffect(() => {
    api.get(`/marketplace/parceiros/${slug}`)
      .then((r) => setItem(r.data))
      .catch((e) => setErr(e.response?.data?.detail || 'Perfil não encontrado'));
  }, [slug]);

  useSeo({
    title: item ? `${item.nome} · ${item.tipo_label} · Marketplace TVDE` : 'Parceiro TVDE',
    description: item ? (item.descricao || '').slice(0, 155) : 'Parceiro do Marketplace TVDE.',
    path: `/parceiros/${slug}`,
    image: item?.logotipo && item.logotipo.startsWith('http') ? item.logotipo : undefined,
  });

  useJsonLd('partner-jsonld', item ? {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    name: item.nome,
    description: (item.descricao || '').slice(0, 300),
    address: { '@type': 'PostalAddress', addressRegion: item.distrito, addressLocality: item.concelho || item.distrito, addressCountry: 'PT' },
    ...(item.contacto?.telefone ? { telephone: item.contacto.telefone } : {}),
    ...(item.contacto?.website ? { url: item.contacto.website } : {}),
  } : {});

  const evento = (tipo) => { api.post(`/marketplace/parceiros/${item.id}/evento`, { tipo }).catch(() => {}); };

  useEffect(() => {
    if (!item?.id) return;
    const key = `mp_partner_view_${item.id}`;
    if (sessionStorage.getItem(key)) return;
    sessionStorage.setItem(key, '1');
    api.post(`/marketplace/parceiros/${item.id}/evento`, { tipo: 'visualizacao' }).catch(() => {});
  }, [item?.id]);

  if (err) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50 px-4">
        <div className="text-center" data-testid="partner-not-found">
          <p className="text-sm font-semibold text-red-600">{err}</p>
          <Link to="/parceiros" className="mt-4 inline-block rounded-xl bg-emerald-600 px-4 py-2 text-sm font-bold text-white">Ver todos os parceiros</Link>
        </div>
      </div>
    );
  }
  if (!item) {
    return <div className="flex min-h-screen items-center justify-center text-sm text-slate-500"><Loader2 className="mr-2 h-4 w-4 animate-spin" /> A carregar...</div>;
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3">
          <Link to="/" className="text-sm font-black text-slate-900">Marketplace <span className="text-emerald-600">TVDE</span></Link>
          <Link to="/parceiros" className="text-xs font-semibold text-slate-500 hover:text-emerald-700" data-testid="partner-back">Voltar aos parceiros</Link>
        </div>
      </header>

      <main className="mx-auto max-w-5xl px-4 py-8">
        <div className="grid gap-6 lg:grid-cols-[1.6fr_1fr]">
          <div>
            <div className="flex items-start gap-4">
              {item.logotipo ? (
                <img src={resolveImg(item.logotipo)} alt={item.nome} className="h-20 w-20 rounded-2xl object-cover" data-testid="partner-logo" />
              ) : (
                <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-slate-100"><Building2 className="h-8 w-8 text-slate-400" /></div>
              )}
              <div className="min-w-0">
                <h1 className="text-3xl font-black tracking-tight text-slate-900 sm:text-4xl" data-testid="partner-name">{item.nome}</h1>
                <p className="mt-1 text-base font-semibold text-emerald-700" data-testid="partner-type">{item.tipo_label}</p>
                <p className="mt-1 inline-flex items-center gap-1 text-sm text-slate-500">
                  <MapPin className="h-4 w-4 text-emerald-500" /> {item.distrito}{item.concelho ? ` · ${item.concelho}` : ''}{item.localidade ? ` · ${item.localidade}` : ''}
                </p>
                {item.destaque && (
                  <span className="mt-2 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2.5 py-1 text-[11px] font-bold uppercase text-amber-800" data-testid="partner-sponsored">
                    <Sparkles className="h-3 w-3" /> Perfil patrocinado
                  </span>
                )}
              </div>
            </div>

            {item.aviso && (
              <p className="mt-4 inline-flex items-start gap-2 rounded-xl bg-amber-50 p-3 text-xs font-semibold text-amber-800" data-testid="partner-paused-notice">
                <AlertTriangle className="mt-0.5 h-4 w-4" /> {item.aviso}
              </p>
            )}

            <p className="mt-5 whitespace-pre-wrap text-base leading-relaxed text-slate-700" data-testid="partner-description">{item.descricao}</p>

            {(item.servicos || []).length > 0 && (
              <div className="mt-5" data-testid="partner-services">
                <h2 className="text-base font-bold text-slate-900 md:text-lg">Serviços</h2>
                <ul className="mt-2 flex flex-wrap gap-2">
                  {item.servicos.map((s) => <li key={s} className="rounded-full bg-white px-3 py-1 text-xs font-semibold text-slate-700 ring-1 ring-slate-200">{s}</li>)}
                </ul>
              </div>
            )}

            {(item.zonas || []).length > 0 && (
              <p className="mt-4 text-sm text-slate-600" data-testid="partner-zones"><strong>Zonas de atuação:</strong> {item.zonas.join(', ')}</p>
            )}
            {item.horario && (
              <p className="mt-2 inline-flex items-start gap-1.5 text-sm text-slate-600" data-testid="partner-hours">
                <Clock className="mt-0.5 h-4 w-4 text-emerald-500" /> {item.horario}
              </p>
            )}

            {Object.keys(item.especificos || {}).length > 0 && (
              <div className="mt-5 rounded-2xl border border-slate-200 bg-white p-4" data-testid="partner-specifics">
                <h2 className="text-base font-bold text-slate-900 md:text-lg">Informação da categoria</h2>
                <dl className="mt-2 grid gap-2 sm:grid-cols-2">
                  {Object.entries(item.especificos).map(([k, v]) => (
                    <div key={k} className="text-sm">
                      <dt className="text-xs uppercase tracking-wide text-slate-400">{k.replace(/_/g, ' ')}</dt>
                      <dd className="font-semibold text-slate-700">{typeof v === 'boolean' ? (v ? 'Sim' : 'Não') : String(v)}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            )}

            {(item.fotos || []).length > 0 && (
              <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-3" data-testid="partner-photos">
                {item.fotos.map((f, i) => <img key={i} src={resolveImg(f)} alt={`${item.nome} ${i + 1}`} className="h-32 w-full rounded-xl object-cover" />)}
              </div>
            )}
          </div>

          <aside>
            <div className="sticky top-6 space-y-3">
              <div className="rounded-2xl border border-slate-200 bg-white p-4">
                <h2 className="text-base font-bold text-slate-900">Contactar</h2>
                <div className="mt-2 space-y-1.5 text-sm">
                  {item.contacto?.telefone && (
                    <a href={`tel:${item.contacto.telefone}`} onClick={() => evento('telefone')} className="flex items-center gap-2 font-semibold text-emerald-700" data-testid="partner-phone">
                      <Phone className="h-4 w-4" /> {item.contacto.telefone}
                    </a>
                  )}
                  {item.contacto?.email && (
                    <a href={`mailto:${item.contacto.email}`} className="flex items-center gap-2 text-slate-600" data-testid="partner-email">
                      <Mail className="h-4 w-4 text-emerald-500" /> {item.contacto.email}
                    </a>
                  )}
                  {item.contacto?.website && (
                    <a href={item.contacto.website} target="_blank" rel="noreferrer nofollow" onClick={() => evento('website')} className="flex items-center gap-2 text-slate-600" data-testid="partner-website">
                      <Globe className="h-4 w-4 text-emerald-500" /> Website
                    </a>
                  )}
                  {item.contacto?.instagram && (
                    <a href={item.contacto.instagram} target="_blank" rel="noreferrer nofollow" className="flex items-center gap-2 text-slate-600" data-testid="partner-instagram">
                      <Instagram className="h-4 w-4 text-emerald-500" /> Instagram
                    </a>
                  )}
                </div>
                {!item.contacto?.telefone && !item.contacto?.email && (
                  <p className="mt-1 text-xs text-slate-500">Este parceiro prefere ser contactado por mensagem.</p>
                )}
              </div>

              {item.aceita_contactos && conversasAtivas && (
                <div className="rounded-2xl border border-slate-200 bg-white p-4" data-testid="partner-conversation-box">
                  <h2 className="inline-flex items-center gap-2 text-base font-bold text-slate-900">
                    <MessageSquare className="h-4 w-4 text-emerald-600" /> Enviar mensagem
                  </h2>
                  <div className="mt-2 flex gap-2">
                    <button type="button" onClick={() => setAssunto('Pedido de contacto')}
                      className={`rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold ${assunto === 'Pedido de contacto' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-300 text-slate-600'}`}
                      data-testid="partner-subject-contact">Pedido de contacto</button>
                    <button type="button" onClick={() => { setAssunto('Pedido de orçamento'); import('@/lib/analytics').then(({ track }) => track('request_partner_quote', { partner_id: item.id, tipo: item.tipo || 'parceiro' })).catch(() => {}); }}
                      className={`rounded-lg border px-2.5 py-1.5 text-[11px] font-semibold ${assunto === 'Pedido de orçamento' ? 'border-emerald-500 bg-emerald-50 text-emerald-700' : 'border-slate-300 text-slate-600'}`}
                      data-testid="partner-subject-quote">Pedir orçamento</button>
                  </div>
                  <div className="mt-3">
                    <ConversationStartForm listingId={item.id} titulo={item.nome} recurso="parceiro" assunto={assunto || 'Pedido de contacto'} />
                  </div>
                </div>
              )}

              <button type="button" onClick={() => setShowReport(true)}
                className="w-full rounded-xl border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-600" data-testid="partner-report">
                <Flag className="mr-1 inline h-3.5 w-3.5" /> Denunciar este perfil
              </button>
            </div>
            <AdSlot posicao="partners_detail_sidebar" distrito={item.distrito || ''} categoria={item.entity_type || ''} />
          </aside>
        </div>
      </main>

      {showReport && <ReportListingModal listingId={item.id} onClose={() => setShowReport(false)} />}
      <MarketplaceFooter />
    </div>
  );
}
